// ** MUI Imports
import IconButton from '@mui/material/IconButton'
import Box, { BoxProps } from '@mui/material/Box'
import { styled, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'

// ** Type Import
import { LayoutProps } from 'src/@core/layouts/types'

// ** Custom Icon Import
import Icon from 'src/@core/components/icon'
import { useSelector } from 'react-redux'
import { RootState } from '@/src/store'
import { Skeleton } from '@mui/material'
import { useGlobalState } from '@/src/pages/_app'
import { Fragment } from 'react'

// ** Configs

interface Props {
  navHover: boolean
  mainNavHidden?: boolean
  collapsedNavWidth: number
  hidden: LayoutProps['hidden']
  navigationBorderWidth: number
  toggleNavVisibility: () => void
  settings: LayoutProps['settings']
  saveSettings: LayoutProps['saveSettings']
  navMenuBranding?: LayoutProps['verticalLayoutProps']['navMenu']['branding']
  menuLockedIcon?: LayoutProps['verticalLayoutProps']['navMenu']['lockedIcon']
  menuUnlockedIcon?: LayoutProps['verticalLayoutProps']['navMenu']['unlockedIcon']
}

// ** Styled Components
const MenuHeaderWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingRight: theme.spacing(3.5),
  transition: 'padding .25s ease-in-out',
  minHeight: theme.mixins.toolbar.minHeight
}))

const SubVerticalNavHeader = (props: Props) => {
  // ** Props
  const {
    // hidden = false,
    navHover,
    settings,
    saveSettings,
    collapsedNavWidth,

    // toggleNavVisibility,
    navigationBorderWidth,
    menuLockedIcon: userMenuLockedIcon,
    navMenuBranding: userNavMenuBranding,
    menuUnlockedIcon: userMenuUnlockedIcon
  } = props

  const { restoData, pending } = useSelector((state: RootState) => state.gyg365RestoList)
  const { franchiseData } = useSelector((state: RootState) => state.gyg365FranchiseList)
  const { isChildPage, isProductPage, isFranchisePage } = useGlobalState()

  // ** Hooks & Vars
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const theme = useTheme()
  const { subNavCollapsed } = settings

  const menuHeaderPaddingLeft = () => {
    if (subNavCollapsed && !navHover) {
      if (userNavMenuBranding) {
        return 0
      } else {
        return (collapsedNavWidth - navigationBorderWidth - 50) / 8
      }
    } else {
      return 6
    }
  }

  const MenuLockedIcon = () => userMenuLockedIcon || <Icon icon='ep:arrow-left-bold' />
  const MenuUnlockedIcon = () => userMenuUnlockedIcon || <Icon icon='ep:arrow-right-bold' />

  const menuCollapsedStyles = subNavCollapsed && !navHover ? { opacity: 0 } : { opacity: 1 }

  return (
    <MenuHeaderWrapper
      className='nav-header'
      sx={{
        position: 'relative',
        pl: menuHeaderPaddingLeft(),
        borderBottom: subNavCollapsed ? '' : '1px solid #646464'
      }}
    >
      {userNavMenuBranding ? (
        userNavMenuBranding(props)
      ) : (
        <Fragment>
          {subNavCollapsed && !navHover && <></>}
          {pending ? (
            <Skeleton variant='rectangular' width={100} height={100} />
          ) : (
            <>
              {!subNavCollapsed && !pending && (
                <Typography
                  variant='h3'
                  fontWeight={700}
                  sx={{
                    ...menuCollapsedStyles,
                    ...(subNavCollapsed && !navHover ? {} : { ml: 2.5 }),
                    color: '#fff'
                  }}
                >
                  {isChildPage &&
                    (restoData?.fullRestaurantName ? restoData.fullRestaurantName : restoData?.restaurantName)}
                  {isProductPage && 'Product List'}
                  {isFranchisePage && franchiseData?.name}
                </Typography>
              )}
            </>
          )}
        </Fragment>
      )}

      <IconButton
        disableRipple
        disableFocusRipple
        onClick={() => saveSettings({ ...settings, subNavCollapsed: !subNavCollapsed })}
        sx={{
          position: 'absolute',
          right: -12,
          backgroundColor: '#646464 !important',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          marginTop: '64px',
          zIndex: 9999,
          p: 0,
          color: 'common.white',
          '& svg': {
            fontSize: '1rem'
          }
        }}
      >
        {subNavCollapsed ? MenuUnlockedIcon() : MenuLockedIcon()}
      </IconButton>
    </MenuHeaderWrapper>
  )
}

export default SubVerticalNavHeader
